import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, Users, DollarSign, AlertTriangle, TrendingUp } from "lucide-react";

export const MeetingCostCalculator = () => {
  const [attendees, setAttendees] = useState(5);
  const [duration, setDuration] = useState(60);
  const [avgHourlyRate, setAvgHourlyRate] = useState(50);
  const [meetingsPerWeek, setMeetingsPerWeek] = useState(3);

  const calculations = useMemo(() => {
    const meetingCost = (attendees * (duration / 60) * avgHourlyRate);
    const weeklyCost = meetingCost * meetingsPerWeek;
    const monthlyCost = weeklyCost * 4.33;
    const yearlyCost = weeklyCost * 52;
    const hoursPerWeek = (attendees * (duration / 60) * meetingsPerWeek);
    const hoursPerYear = hoursPerWeek * 52;
    
    return {
      meetingCost,
      weeklyCost,
      monthlyCost,
      yearlyCost,
      hoursPerWeek,
      hoursPerYear,
    };
  }, [attendees, duration, avgHourlyRate, meetingsPerWeek]);

  const formatCurrency = (value: number) => 
    value.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <p className="text-muted-foreground">
          Calculate the true cost of meetings to your organization and identify opportunities to save time and money.
        </p>
      </div>

      {/* Input Controls */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" />
                Attendees
              </Label>
              <Badge variant="secondary">{attendees}</Badge>
            </div>
            <Slider
              value={[attendees]}
              onValueChange={(v) => setAttendees(v[0])}
              min={2}
              max={30}
              step={1}
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                Duration (minutes)
              </Label>
              <Badge variant="secondary">{duration} min</Badge>
            </div>
            <Slider
              value={[duration]}
              onValueChange={(v) => setDuration(v[0])}
              min={15}
              max={180}
              step={15}
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-3">
            <Label className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-primary" />
              Avg Hourly Rate ($)
            </Label>
            <Input
              type="number"
              value={avgHourlyRate}
              onChange={(e) => setAvgHourlyRate(Number(e.target.value) || 0)}
              min={10}
              max={500}
              className="text-lg"
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                Meetings/Week
              </Label>
              <Badge variant="secondary">{meetingsPerWeek}</Badge>
            </div>
            <Slider
              value={[meetingsPerWeek]}
              onValueChange={(v) => setMeetingsPerWeek(v[0])}
              min={1}
              max={20}
              step={1}
            />
          </CardContent>
        </Card>
      </div>

      {/* Results */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground mb-1">Per Meeting</p>
            <p className="text-2xl font-bold text-primary">
              {formatCurrency(calculations.meetingCost)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground mb-1">Weekly Cost</p>
            <p className="text-2xl font-bold text-foreground">
              {formatCurrency(calculations.weeklyCost)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground mb-1">Monthly Cost</p>
            <p className="text-2xl font-bold text-foreground">
              {formatCurrency(calculations.monthlyCost)}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-destructive/5 border-destructive/20 sm:col-span-2 lg:col-span-3">
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-destructive" />
              <div>
                <p className="text-sm text-muted-foreground">Annual Meeting Cost</p>
                <p className="text-3xl font-bold text-destructive">
                  {formatCurrency(calculations.yearlyCost)}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Time Spent in Meetings</p>
              <p className="text-xl font-semibold text-foreground">
                {calculations.hoursPerYear.toLocaleString()} hours/year
              </p>
              <p className="text-xs text-muted-foreground">
                ({calculations.hoursPerWeek.toFixed(1)} hours/week)
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tips */}
      <Card>
        <CardContent className="p-4">
          <h4 className="font-semibold text-foreground mb-3">💡 Cost Reduction Tips</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Reduce meeting duration by 25% — most 1-hour meetings can be done in 45 minutes</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Limit attendees to decision-makers only — share notes with others async</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Replace status meetings with async updates via Slack or project management tools</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Implement "No Meeting Wednesdays" for deep work time</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};
